// Pure calculations over the data - no React, easy to unit test or move server-side

import { Budget, Category, MonthKey, Transaction } from "./types";
import { monthOf, shiftMonth } from "./format";

export interface MonthTotals {
    month: MonthKey;
    income: number;
    expenses: number;
}

export function inMonth(transactions: Transaction[], month: MonthKey): Transaction[] {
    return transactions.filter(t => monthOf(t.date) === month);
}

export function totals(transactions: Transaction[]): { income: number; expenses: number; net: number; savingsRate: number | null } {
    let income = 0;
    let expenses = 0;
    transactions.forEach(t => {
        if (t.type === "income") {
            income += t.amount;
        } else {
            expenses += t.amount;
        }
    });
    const net = income - expenses;
    return { income, expenses, net, savingsRate: income > 0 ? net / income : null };
}

export function monthlyTrend(transactions: Transaction[], endMonth: MonthKey, months: number): MonthTotals[] {
    const result: MonthTotals[] = [];
    for (let offset = months - 1; offset >= 0; offset--) {
        const month = shiftMonth(endMonth, -offset);
        const t = totals(inMonth(transactions, month));
        result.push({ month, income: t.income, expenses: t.expenses });
    }
    return result;
}

export interface CategorySpend {
    category: Category;
    amount: number;
    share: number;
}

// Expense categories with spending in the period, largest first
export function spendingByCategory(transactions: Transaction[], categories: Category[]): CategorySpend[] {
    const sums: Record<string, number> = {};
    let total = 0;
    transactions.forEach(t => {
        if (t.type === "expense") {
            sums[t.categoryId] = (sums[t.categoryId] || 0) + t.amount;
            total += t.amount;
        }
    });
    return categories
        .filter(c => sums[c.id] > 0)
        .map(c => ({ category: c, amount: sums[c.id], share: total > 0 ? sums[c.id] / total : 0 }))
        .sort((a, b) => b.amount - a.amount);
}

export type BudgetStatus = "good" | "warning" | "critical";

export interface BudgetProgress {
    budget: Budget;
    category: Category;
    spent: number;
    ratio: number;
    status: BudgetStatus;
}

export function budgetProgress(transactions: Transaction[], budgets: Budget[], categories: Category[]): BudgetProgress[] {
    return budgets
        .map(budget => {
            const category = categories.find(c => c.id === budget.categoryId);
            if (!category) {
                return null;
            }
            const spent = transactions
                .filter(t => t.type === "expense" && t.categoryId === budget.categoryId)
                .reduce((sum, t) => sum + t.amount, 0);
            const ratio = budget.limit > 0 ? spent / budget.limit : 0;
            const status: BudgetStatus = ratio > 1 ? "critical" : ratio >= 0.85 ? "warning" : "good";
            return { budget, category, spent, ratio, status };
        })
        .filter((b): b is BudgetProgress => b !== null);
}
